import { useEffect, useState } from 'react';
import { ListSkeleton } from '../../components/ListSkeleton';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router';
import { Plus, Search, X } from 'lucide-react';
import { Button } from '@repo/ui/src/components/ui/button';
import { Input } from '@repo/ui/src/components/ui/input';
import { Label } from '@repo/ui/src/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@repo/ui/src/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@repo/ui/src/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@repo/ui/src/components/ui/select';
import { cn } from '@repo/ui/src/lib/utils';
import type { CreateClientInput } from '@repo/validation';
import { useClients, useClientTags, useCreateClient, useClientLookup } from '../../hooks/useClients';
import { formatVnd } from '../../lib/formatVnd';
import { useCan } from '../../hooks/useCan';
import { AdminOnlyTooltip } from '../../components/AdminOnlyTooltip';
import { PageHeader, pageHeaderActionClass } from '../../components/PageHeader';
import { PaginationFooter, pageCount } from '../../components/PaginationFooter';
import { Tags } from '../../components/Tags';
import { CardList, CardRow } from '../../components/CardList';

const SEARCH_DEBOUNCE_MS = 300;
const SORTS = ['newest', 'oldest', 'name'] as const;
type Sort = (typeof SORTS)[number];

export function ClientsPage() {
  const { t } = useTranslation('clients');
  const [params, setParams] = useSearchParams();
  const search = params.get('search') ?? '';
  const tag = params.get('tag') ?? '';
  const sortParam = params.get('sort');
  const sort: Sort = SORTS.includes(sortParam as Sort) ? (sortParam as Sort) : 'newest';
  const page = Number(params.get('page')) || 1;

  const [searchInput, setSearchInput] = useState(search);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const canManageClients = useCan('manage:clients');

  // Any change to search/tag/sort resets to page 1 — page=1 itself is kept
  // out of the URL so links stay clean. Uses the functional form of setParams
  // so it always applies against the CURRENT params at commit time, not
  // whatever `params` looked like when the caller was created — this matters
  // for the debounced search effect below, whose timer can fire after a
  // tag/sort click has already updated the URL (mirrors StudentsPage).
  const setFilter = (key: string, value: string | null) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value) next.set(key, value);
      else next.delete(key);
      next.delete('page');
      return next;
    }, { replace: true });
  };

  const setPage = (next: number) => {
    setParams((prev) => {
      const p = new URLSearchParams(prev);
      if (next <= 1) p.delete('page');
      else p.set('page', String(next));
      return p;
    }, { replace: true });
  };

  // Debounce search input → URL
  useEffect(() => {
    const timer = setTimeout(() => {
      // No-op debounce (e.g. a shared/refreshed URL landing with `search`
      // already set to this value) must not call setFilter — that would
      // strip `page` from the URL even though nothing actually changed.
      if (searchInput === search) return;
      setFilter('search', searchInput || null);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const { data, isLoading } = useClients({
    search: search || undefined,
    tag: tag || undefined,
    sort: sort === 'newest' ? undefined : sort,
    page: page > 1 ? page : undefined,
  });
  const { data: allTags } = useClientTags();

  // A page beyond the last one (stale link, or the last item on the last
  // page got deleted) would otherwise render the "no clients at all" empty
  // state instead of the real last page.
  useEffect(() => {
    if (data && data.items.length === 0 && data.total > 0 && page > 1) {
      setPage(pageCount(data.total, data.pageSize));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const setTag = (next: string | null) => setFilter('tag', next);
  const setSort = (next: Sort) => setFilter('sort', next === 'newest' ? null : next);

  const clearAll = () => {
    setSearchInput('');
    setParams((prev) => {
      const p = new URLSearchParams(prev);
      p.delete('search');
      p.delete('tag');
      p.delete('page');
      return p;
    }, { replace: true });
  };

  const hasActiveFilter = Boolean(search || tag);

  return (
    <div className="p-4 sm:p-8 max-w-6xl mx-auto w-full">
      <PageHeader
        size="lg"
        title={t('list.title')}
        subtitle={t('list.subtitle')}
        extra={
          <AdminOnlyTooltip show={!canManageClients} className="flex-1 sm:flex-none">
            <Button
              size="sm"
              onClick={() => setIsDialogOpen(true)}
              className={cn(pageHeaderActionClass, 'gap-2')}
              disabled={!canManageClients}
            >
              <Plus className="h-4 w-4" /> {t('list.add')}
            </Button>
          </AdminOnlyTooltip>
        }
      />

      {/* Search + sort */}
      <div className="flex gap-3 mb-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-surface-400" />
          <Input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder={t('list.search')}
            className="pl-9"
          />
        </div>
        <Select value={sort} onValueChange={(v) => setSort(v as Sort)}>
          <SelectTrigger className="w-[112px] sm:w-[150px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="newest">{t('list.sortNewest')}</SelectItem>
            <SelectItem value="oldest">{t('list.sortOldest')}</SelectItem>
            <SelectItem value="name">{t('list.sortName')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Tag filter chips */}
      {((allTags && allTags.length > 0) || hasActiveFilter) && (
        <div className="flex flex-wrap items-center gap-2 mb-6">
          {(allTags ?? []).map((tagName) => {
            const isActive = tag === tagName;
            return (
              <button
                key={tagName}
                type="button"
                onClick={() => setTag(isActive ? null : tagName)}
                className={cn(
                  'inline-flex items-center h-7 px-3 rounded-full text-xs font-medium border transition-colors',
                  isActive
                    ? 'bg-brand-500 text-white border-brand-500'
                    : 'bg-surface-0 text-surface-600 border-surface-200 hover:bg-surface-50',
                )}
              >
                {tagName}
              </button>
            );
          })}
          {hasActiveFilter && (
            <button
              type="button"
              onClick={clearAll}
              className="inline-flex items-center gap-1 h-7 px-3 text-xs text-surface-500 hover:text-surface-700"
            >
              <X className="w-3 h-3" /> {t('list.clearFilter')}
            </button>
          )}
        </div>
      )}

      <div className="rounded-md border bg-card">
        {isLoading ? (
          <ListSkeleton label={t('list.loading')} />
        ) : !data || data.items.length === 0 ? (
          <p className="flex h-24 items-center justify-center text-sm text-muted-foreground">
            {hasActiveFilter ? t('list.noResults') : t('list.empty')}
          </p>
        ) : (
          <>
            <div className="hidden md:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('list.name')}</TableHead>
                    <TableHead>{t('list.email')}</TableHead>
                    <TableHead>{t('list.zaloPhone')}</TableHead>
                    <TableHead>{t('list.added')}</TableHead>
                    <TableHead className="text-right">{t('list.outstanding')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.items.map((client) => (
                    <TableRow key={client.id} className="cursor-pointer">
                      <TableCell>
                        <Link to={`/clients/${client.id}`} className="block">
                          <div className="font-medium text-surface-900">{client.name}</div>
                          {client.tags.length > 0 && (
                            <div className="mt-1">
                              <Tags tags={client.tags} mode="read" />
                            </div>
                          )}
                        </Link>
                      </TableCell>
                      <TableCell>
                        <Link to={`/clients/${client.id}`} className="block text-surface-600">
                          {client.email || '—'}
                        </Link>
                      </TableCell>
                      <TableCell>
                        <Link to={`/clients/${client.id}`} className="block text-surface-600">
                          {client.zaloPhone || '—'}
                        </Link>
                      </TableCell>
                      <TableCell>
                        <Link to={`/clients/${client.id}`} className="block text-surface-600">
                          {new Date(client.createdAt).toLocaleDateString('vi-VN')}
                        </Link>
                      </TableCell>
                      <TableCell className="text-right">
                        <Link
                          to={`/clients/${client.id}`}
                          className={cn(
                            'block tabular-nums',
                            // Ochre is the app's "needs attention" money colour;
                            // a settled payer stays quiet grey rather than
                            // shouting a zero.
                            client.outstandingAmount > 0 ? 'font-medium text-attention-foreground' : 'text-surface-500',
                          )}
                        >
                          {client.outstandingAmount > 0 ? formatVnd(client.outstandingAmount) : '—'}
                        </Link>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <CardList>
              {data.items.map((client) => (
                <CardRow
                  key={client.id}
                  to={`/clients/${client.id}`}
                  title={client.name}
                  // 13 §7: line 2 is the Zalo number. NOT a `tel:` link — the
                  // whole card is the link to the customer, and tap-to-call
                  // lives on their own page (13 §2 identity block), where it
                  // already is. Tags and email are reference facts and drop off
                  // the phone card; both are on the detail page.
                  line2={client.zaloPhone || '—'}
                  right={
                    client.outstandingAmount > 0 ? (
                      <span className="text-attention-foreground">{formatVnd(client.outstandingAmount)}</span>
                    ) : (
                      <span className="font-normal text-surface-400">—</span>
                    )
                  }
                />
              ))}
            </CardList>
          </>
        )}
      </div>

      {data && (
        <PaginationFooter
          page={data.page}
          pageSize={data.pageSize}
          total={data.total}
          onPageChange={setPage}
          summary={t('list.pageOf', {
            page: data.page,
            pages: pageCount(data.total, data.pageSize),
            total: data.total,
          })}
          prevLabel={t('list.prev')}
          nextLabel={t('list.next')}
        />
      )}

      <CreateClientDialog open={isDialogOpen} onOpenChange={setIsDialogOpen} />
    </div>
  );
}

function CreateClientDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation('clients');
  const { mutate: createClient, isPending } = useCreateClient();
  const [formData, setFormData] = useState<CreateClientInput>({
    name: '',
    email: '',
    zaloPhone: '',
  });
  // Fires only on blur (not per keystroke) — see useClientLookup.
  const [lookupPhone, setLookupPhone] = useState('');
  const { data: dupClient } = useClientLookup(lookupPhone);
  const showDupWarning =
    Boolean(dupClient) && lookupPhone.trim() === (formData.zaloPhone ?? '').trim();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createClient(formData, {
      onSuccess: () => {
        onOpenChange(false);
        setFormData({ name: '', email: '', zaloPhone: '' });
        setLookupPhone('');
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{t('create.title')}</DialogTitle>
          <DialogDescription>{t('create.description')}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name">{t('create.name')}</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={t('create.namePlaceholder')}
                required
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="email">{t('create.email')}</Label>
              <Input
                id="email"
                type="email"
                value={formData.email || ''}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder={t('create.emailPlaceholder')}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="zaloPhone">{t('create.zaloPhone')}</Label>
              <Input
                id="zaloPhone"
                value={formData.zaloPhone || ''}
                onChange={(e) => setFormData({ ...formData, zaloPhone: e.target.value })}
                onBlur={(e) => setLookupPhone(e.target.value)}
                placeholder={t('create.zaloPhonePlaceholder')}
              />
              {showDupWarning && dupClient && (
                <p className="text-xs text-amber-600">
                  {t('create.dupPayerWarning', { name: dupClient.name })}
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('create.cancel')}
            </Button>
            <Button type="submit" disabled={isPending || !formData.name}>
              {isPending ? t('create.submitting') : t('create.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
